import { useEffect, useRef } from 'react';
import { useProjectId } from 'store/project';
import { useAppUrlParams, UrlParamName } from 'hooks/navigation/useAppUrlParams';
import { useDocumentationStore } from '../../store';
import type { DocumentationUrlTarget } from '../../slices/moduleLifecycle/types';

/**
 * Documentation module mount hook - same role as `useSitesInitializer` /
 * `useIssuesInitializer`. The store owns the lifecycle (`openDocumentationModule`)
 * and the channel subscription; this hook only binds them to the mounted view.
 *
 * Call only on views that use the documentation module (grid, browse dialog,
 * map overlays).
 */
export function useDocumentationInitializer(): void {
  const projectId = useProjectId();
  const urlTarget: DocumentationUrlTarget = {
    directoryId: useAppUrlParams(UrlParamName.DIRECTORY_ID),
    fileId: useAppUrlParams(UrlParamName.FILE_ID),
  };
  const openDocumentationModule = useDocumentationStore.use.openDocumentationModule();
  const subscribeToChannelMessages =
    useDocumentationStore.use.subscribeToChannelMessages();

  // The URL is read when the module opens; later URL changes are handled by the
  // URL synchronization hooks, so they must not reopen (reload) the module.
  const urlTargetRef = useRef(urlTarget);
  urlTargetRef.current = urlTarget;

  // Service worker broadcasts (documents, directories) while mounted.
  useEffect(() => subscribeToChannelMessages(), [subscribeToChannelMessages]);

  useEffect(
    () => openDocumentationModule(projectId, urlTargetRef.current),
    [projectId, openDocumentationModule]
  );
}
